import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import JobCard from './components/JobCard';
import ApplicationStatusCard from './components/ApplicationStatusCard';
import QuickActionCard from './components/QuickActionCard';
import StatsCard from './components/StatsCard';
import InterviewScheduleCard from './components/InterviewScheduleCard';
import ProfileCompletionCard from './components/ProfileCompletionCard';
import SearchBar from './components/SearchBar';
import jobService from '../../services/jobService';

const JobSeekerDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('recommended');
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      const jobs = await jobService.getPublicJobs();
      setRecommendedJobs(jobs);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError(err?.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const recentApplications = [
  {
    id: 1,
    jobTitle: "Senior React Developer",
    company: "Tech Innovations Pvt Ltd",
    companyLogo: "https://images.unsplash.com/photo-1606400275765-b800c01225e5",
    companyLogoAlt: "Modern tech company office building with glass facade",
    status: "Under Review",
    appliedDate: "2 days ago"
  },
  {
    id: 2,
    jobTitle: "Full Stack Developer",
    company: "Digital Solutions India",
    companyLogo: "https://images.unsplash.com/photo-1631856623216-d1fcecbf5b45",
    companyLogoAlt: "Corporate office building with modern architecture",
    status: "Shortlisted",
    appliedDate: "5 days ago"
  },
  {
    id: 3,
    jobTitle: "Frontend Developer",
    company: "StartUp Hub Technologies",
    companyLogo: "https://images.unsplash.com/photo-1556761175-4b46a572b786",
    companyLogoAlt: "Modern startup office space with open layout",
    status: "Interview Scheduled",
    appliedDate: "1 week ago"
  },
  {
    id: 4,
    jobTitle: "React Native Developer",
    company: "Mobile First Solutions",
    companyLogo: "https://images.unsplash.com/photo-1662019293071-bff94b65d33e",
    companyLogoAlt: "Contemporary tech office with minimalist design",
    status: "Applied",
    appliedDate: "3 days ago"
  }];


  const quickActions = [
  {
    id: 1,
    title: "Update Profile",
    description: "Keep your profile up-to-date to attract better opportunities",
    icon: "User",
    iconColor: "var(--color-primary)",
    bgColor: "bg-primary/10",
    path: "/user-profile-settings"
  },
  {
    id: 2,
    title: "Upload Resume",
    description: "Add your latest resume to improve job matches",
    icon: "FileText",
    iconColor: "var(--color-success)",
    bgColor: "bg-success/10",
    path: "/user-profile-settings"
  },
  {
    id: 3,
    title: "Set Job Alerts",
    description: "Get notified about relevant job opportunities",
    icon: "Bell",
    iconColor: "var(--color-warning)",
    bgColor: "bg-warning/10",
    path: "/user-profile-settings"
  },
  {
    id: 4,
    title: "Saved Jobs",
    description: "View and manage your bookmarked job listings",
    icon: "Bookmark",
    iconColor: "var(--color-accent)",
    bgColor: "bg-accent/10",
    path: "/job-listings"
  }];


  const statsData = [
  {
    id: 1,
    label: "Profile Views",
    value: "247",
    icon: "Eye",
    iconColor: "var(--color-primary)",
    bgColor: "bg-primary/10",
    trend: "+12%",
    trendUp: true
  },
  {
    id: 2,
    label: "Applications Sent",
    value: "18",
    icon: "Send",
    iconColor: "var(--color-success)",
    bgColor: "bg-success/10",
    trend: "+5",
    trendUp: true
  },
  {
    id: 3,
    label: "Interview Calls",
    value: "3",
    icon: "Phone",
    iconColor: "var(--color-warning)",
    bgColor: "bg-warning/10",
    trend: "+2",
    trendUp: true
  },
  {
    id: 4,
    label: "Saved Jobs",
    value: "12",
    icon: "Bookmark",
    iconColor: "var(--color-accent)",
    bgColor: "bg-accent/10"
  }];


  const upcomingInterviews = [
  {
    id: 1,
    jobTitle: "Frontend Developer",
    company: "StartUp Hub Technologies",
    companyLogo: "https://images.unsplash.com/photo-1556761175-4b46a572b786",
    companyLogoAlt: "Modern startup office space with open layout",
    date: "28 Nov 2025",
    time: "10:00 AM - 11:00 AM",
    mode: "Video Call"
  },
  {
    id: 2,
    jobTitle: "Full Stack Developer",
    company: "Digital Solutions India",
    companyLogo: "https://images.unsplash.com/photo-1631856623216-d1fcecbf5b45",
    companyLogoAlt: "Corporate office building with modern architecture",
    date: "30 Nov 2025",
    time: "2:00 PM - 3:30 PM",
    mode: "In-Person"
  }];


  const handleSaveJob = (jobId, isSaved) => {
    console.log(`Job ${jobId} ${isSaved ? 'saved' : 'unsaved'}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-8 px-4 lg:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
              Welcome back, Priya! 👋
            </h1>
            <p className="text-muted-foreground">
              Here's what's happening with your job search today
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 space-y-6">
              <SearchBar />

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {statsData?.map((stat) =>
                <StatsCard key={stat?.id} stat={stat} />
                )}
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-foreground">Recommended Jobs</h2>
                  <button
                    onClick={() => navigate('/job-listings')}
                    className="text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-smooth">
                    View All
                    <Icon name="ArrowRight" size={16} />
                  </button>
                </div>

                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                  <button
                    onClick={() => setActiveTab('recommended')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-smooth ${
                    activeTab === 'recommended' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'}`
                    }>

                    Recommended
                  </button>
                  <button
                    onClick={() => setActiveTab('recent')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-smooth ${
                    activeTab === 'recent' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'}`
                    }>

                    Recent
                  </button>
                  <button
                    onClick={() => setActiveTab('remote')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-smooth ${
                    activeTab === 'remote' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/80'}`
                    }>

                    Remote
                  </button>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  </div>
                ) : error ? (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
                    <Icon name="AlertCircle" size={48} color="var(--color-destructive)" className="mx-auto mb-4" />
                    <p className="text-destructive font-medium mb-2">Failed to load jobs</p>
                    <p className="text-sm text-muted-foreground mb-4">{error}</p>
                    <button
                      onClick={fetchJobs}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth">
                      Try Again
                    </button>
                  </div>
                ) : recommendedJobs?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="Briefcase" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                    <p className="text-muted-foreground">No jobs available at the moment</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recommendedJobs?.slice(0, 4)?.map((job) => (
                      <JobCard
                        key={job?.id}
                        job={job}
                        onSave={handleSaveJob}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-foreground">Recent Applications</h2>
                  <button
                    onClick={() => navigate('/application-tracking')}
                    className="text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-smooth">

                    View All
                    <Icon name="ArrowRight" size={16} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {recentApplications?.map((application) =>
                  <ApplicationStatusCard
                    key={application?.id}
                    application={application} />

                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-4 space-y-6">
              <ProfileCompletionCard
                completionPercentage={75}
                missingItems={[
                "Add work experience",
                "Upload resume",
                "Add skills assessment"]
                } />


              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  {quickActions?.map((action) =>
                  <QuickActionCard key={action?.id} action={action} />
                  )}
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">Upcoming Interviews</h3>
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-primary">{upcomingInterviews?.length}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  {upcomingInterviews?.map((interview) =>
                  <InterviewScheduleCard
                    key={interview?.id}
                    interview={interview} />

                  )}
                </div>
              </div>

              <div className="bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20 rounded-lg p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="Lightbulb" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">Job Search Tip</h4>
                    <p className="text-xs text-muted-foreground">
                      Customize your resume for each application to increase your chances of getting shortlisted by 40%
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default JobSeekerDashboard;